/**
 * Normalizes a track's speed + heading from its provider-specific props.
 * Air feeds carry `velocity` (m/s) + `heading`; maritime feeds carry `speed`
 * (knots, SOG) + `course` (COG). One lookup, no per-module branching upstream.
 */

import type { Track } from '@vtak/shared';

/** Knots → metres per second */
const KNOTS_TO_MS = 0.514444;

/** A track's motion in SI units for dead reckoning */
export interface TrackKinematics {
  speedMs: number;
  headingDeg: number;
}

/** Coerce a prop value to a finite number, or null */
function asNumber(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Extract speed (m/s) and heading (deg) from a track, or null when the track
 * has no usable motion (missing fields or zero speed — a stationary target has
 * no velocity vector to draw).
 */
export function trackKinematics(track: Track): TrackKinematics | null {
  const air = airKinematics(track);
  if (air) return air;
  return maritimeKinematics(track);
}

/** Air tracks: props.velocity (m/s) + props.heading (deg) */
function airKinematics(track: Track): TrackKinematics | null {
  const speedMs = asNumber(track.props['velocity']);
  const headingDeg = asNumber(track.props['heading']);
  if (speedMs === null || headingDeg === null || speedMs <= 0) return null;
  return { speedMs, headingDeg };
}

/** Maritime tracks: props.speed (knots, SOG) + props.course (deg, COG) */
function maritimeKinematics(track: Track): TrackKinematics | null {
  const speedKnots = asNumber(track.props['speed']);
  const headingDeg = asNumber(track.props['course']);
  if (speedKnots === null || headingDeg === null || speedKnots <= 0) return null;
  return { speedMs: speedKnots * KNOTS_TO_MS, headingDeg };
}
